import React, { useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  FormControlLabel,
  Switch,
  TextField,
  Typography,
} from '@mui/material';
import { getApiErrorMessage } from '../../lib/apiError';
import { useProfile } from '../../hooks/useProfile';

export const TwoFactorCard: React.FC = () => {
  const { user, startTwoFactorSetup, confirmTwoFactorSetup, disableTwoFactor } = useProfile();

  const isEnabled = user?.twoFactorEnabled ?? false;

  const [isBusy, setIsBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  // Setup dialog state. The switch itself always reflects the confirmed server
  // state, never this pending intent, so an abandoned setup cannot leave the UI
  // claiming 2FA is on when it is not.
  const [setupOpen, setSetupOpen] = useState(false);
  const [sentToEmail, setSentToEmail] = useState('');
  const [code, setCode] = useState('');
  const [dialogError, setDialogError] = useState<string | null>(null);

  const handleToggle = async (checked: boolean) => {
    setError(null);
    setSuccess(null);

    if (checked) {
      setIsBusy(true);
      try {
        const email = await startTwoFactorSetup();
        setSentToEmail(email);
        setCode('');
        setDialogError(null);
        setSetupOpen(true);
      } catch (err) {
        setError(getApiErrorMessage(err, 'Could not send a verification code.'));
      } finally {
        setIsBusy(false);
      }
      return;
    }

    setIsBusy(true);
    try {
      await disableTwoFactor();
      setSuccess('Two-factor authentication is now off.');
    } catch (err) {
      setError(getApiErrorMessage(err, 'Could not turn off two-factor authentication.'));
    } finally {
      setIsBusy(false);
    }
  };

  const handleConfirm = async () => {
    setDialogError(null);
    setIsBusy(true);
    try {
      await confirmTwoFactorSetup(code.trim());
      setSetupOpen(false);
      setCode('');
      setSuccess('Two-factor authentication is now on. You will be asked for a code next time you sign in.');
    } catch (err) {
      setDialogError(getApiErrorMessage(err, 'That code could not be verified.'));
    } finally {
      setIsBusy(false);
    }
  };

  const handleCancel = () => {
    setSetupOpen(false);
    setCode('');
    setDialogError(null);
  };

  return (
    <>
      <Card>
        <CardContent>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
            <Typography variant="h6">Two-factor authentication</Typography>
            <Chip
              size="small"
              label={isEnabled ? 'On' : 'Off'}
              color={isEnabled ? 'success' : 'default'}
            />
          </Box>

          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            When this is on, signing in also needs a 6-digit code sent to your email address. It
            protects your account if someone learns your password.
          </Typography>

          {error && (
            <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
              {error}
            </Alert>
          )}
          {success && (
            <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess(null)}>
              {success}
            </Alert>
          )}

          <FormControlLabel
            control={
              <Switch
                checked={isEnabled}
                onChange={(e) => handleToggle(e.target.checked)}
                disabled={isBusy}
              />
            }
            label={isEnabled ? 'Enabled' : 'Disabled'}
          />
          {isBusy && <CircularProgress size={18} sx={{ ml: 2 }} />}
        </CardContent>
      </Card>

      <Dialog open={setupOpen} onClose={handleCancel} maxWidth="xs" fullWidth>
        <DialogTitle>Confirm your email</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mb: 2 }}>
            We sent a 6-digit code to <strong>{sentToEmail}</strong>. Enter it below to finish
            turning on two-factor authentication. The code expires in 10 minutes.
          </DialogContentText>

          {dialogError && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {dialogError}
            </Alert>
          )}

          <TextField
            label="6-digit code"
            fullWidth
            autoFocus
            value={code}
            onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
            disabled={isBusy}
            inputProps={{ inputMode: 'numeric', maxLength: 6 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCancel} disabled={isBusy}>
            Cancel
          </Button>
          <Button variant="contained" onClick={handleConfirm} disabled={isBusy || code.length !== 6}>
            {isBusy ? <CircularProgress size={22} color="inherit" /> : 'Turn on'}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};
